import React, { createRef, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Thumbs, FreeMode, Zoom, Navigation } from 'swiper';
import 'swiper/css/bundle';
import 'swiper/css/zoom';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import clsx from 'clsx';
import ProductItemImage from '../../Types/ProductItemImage';
import { ResponsiveImageModel } from '../../Types/ResponsiveImageModel';
import useReady from '../../Hooks/useReady';

interface FullscreenImageSliderProps {
  showCloseButton?: boolean;
  images?: ProductItemImage[];
  videoUrl?: string;
  initialSlide?: number;
  onClosed?: () => void;
}

interface MediaItem {
  type: 'image' | 'video';
  image?: ProductItemImage;
  videoUrl?: string;
}

const FullscreenImageSlider = ({
  showCloseButton = true,
  images = [],
  initialSlide = 0,
  videoUrl,
  onClosed,
}: FullscreenImageSliderProps) => {
  const dispatch = useDispatch();
  const contentRef = createRef<HTMLDivElement>();

  const [open, setOpen] = useState(true);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const [hasLoadedOriginalImage, setHasLoadedOriginalImage] = useState<
    number[]
  >([]);
  const imageCount = useMemo(() => images.length, [images]);

  const onKeyDown = (e) => {
    if (e.key == 'Escape') {
      setOpen(false);
    }
  };

  const mediaList: MediaItem[] = useMemo(() => {
    let mediaList: MediaItem[] = images.map((img) => ({
      type: 'image',
      image: img,
    }));
    if (videoUrl) {
      if (mediaList.length > 0) {
        mediaList.splice(1, 0, { type: 'video', videoUrl });
      } else {
        mediaList.push({ type: 'video', videoUrl });
      }
    }
    return mediaList;
  }, [images, videoUrl]);

  const isReady = useReady();

  useEffect(() => {
    dispatch(
      addOverlay('FullscreenImageSlider', () => {
        setOpen(false);
      })
    );
    document.addEventListener('keydown', onKeyDown, false);

    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      dispatch(removeOverlay('FullscreenImageSlider'));
      onClosed && onClosed();
    }
  }, [dispatch, open, onClosed]);

  const onZoom = (slideIndex: number, scale: number) => {
    setIsZoomed(scale > 1);
    if (!hasLoadedOriginalImage.includes(slideIndex)) {
      setHasLoadedOriginalImage((hasLoadedOriginalImage) => [
        ...hasLoadedOriginalImage,
        slideIndex,
      ]);
    }
  };

  return open ? (
    <div
      className="fixed bottom-0 left-0 right-0 top-0 z-[55] h-screen w-full bg-white"
      id="fullscreen__image-slider"
    >
      <div className="duration-125 absolute left-4 top-4 z-[60] flex items-center rounded-full bg-opacity-white-90 px-3 py-2.5 text-md leading-tight text-gray-900 transition-opacity ease-in-out [&_svg]:mr-1">
        <Icon name="info" />
        {translate('fullscreen-image-slider.double-click-to-zoom')}
      </div>
      <div className="h-full" ref={contentRef}>
        {showCloseButton && (
          <button
            className="absolute right-2 top-2 z-10 flex items-center justify-center rounded-full bg-opacity-white-90 p-2.5 transition-all"
            onClick={() => setOpen(false)}
          >
            <Icon className="h-5 w-5" name="close" />
          </button>
        )}

        <Swiper
          className="h-full [&_.swiper-pagination]:!bottom-8 [&_.swiper-slide-active]:flex [&_.swiper-slide-active]:items-center [&_.swiper-slide-active]:justify-center"
          loop={true}
          slidesPerView={1}
          initialSlide={initialSlide}
          zoom={{
            toggle: true,
          }}
          modules={[Pagination, Navigation, Thumbs, FreeMode, Zoom]}
          onZoomChange={(swiper, scale) => onZoom(swiper.realIndex, scale)}
          pagination={{
            type: 'fraction',
          }}
          navigation={true}
          threshold={5}
        >
          {mediaList.map((item, idx) => (
            <SwiperSlide
              key={'fullwidth-image-slide' + idx}
              zoom={item.type == 'image'}
            >
              {isReady && item.type == 'video' && (
                <video
                  className="h-full w-full object-contain"
                  src={item.videoUrl}
                  autoPlay
                  muted
                  playsInline
                  loop
                />
              )}
              {item.type == 'image' && (
                <img
                  className="h-full w-full object-contain"
                  src={
                    isZoomed || hasLoadedOriginalImage.includes(idx)
                      ? item.image?.originalSrc
                      : item.image?.hdSrc
                  }
                  loading={idx == 0 ? 'eager' : 'lazy'}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  ) : null;
};

export default FullscreenImageSlider;
